import React from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, RefreshCw, Send, XCircle, AlertTriangle, Download, Sparkles } from 'lucide-react';
import { Stat, DetailsTable } from './workbenchShared';
import { EmployeePreflight, PushHistory } from './PushPreflight';
import { QboReconcile } from './QboReconcile';
import { PushedDayLocks, VerificationBadge } from './PushedDayLocks';

// Per-worker include/exclude for THIS push only (persistent exemptions live on the staff profile).
const WorkerIncludeList = ({ details = [], excludedIds = [], onToggle }) => {
  const workers = [];
  const seen = new Map();
  for (const d of details) {
    if (!['ready', 'error', 'exempt'].includes(d.status)) continue;
    let w = seen.get(d.worker_id);
    if (!w) { w = { id: d.worker_id, name: d.worker_name, ready: 0, errors: 0, exempt: false }; seen.set(d.worker_id, w); workers.push(w); }
    if (d.status === 'ready') w.ready += 1;
    if (d.status === 'error') w.errors += 1;
    if (d.status === 'exempt') w.exempt = true;
  }
  if (workers.length === 0) return null;
  return (
    <div className="rounded border border-[#1F2D3F] p-2" data-testid="pw-include-list">
      <p className="text-[11px] mb-1.5" style={{ color: '#94A3B8' }}>
        <b className="text-slate-200">Who goes in this push</b> — untick anyone to leave them out this time only (e.g. still being set up in QuickBooks). Staff who are never paid through QuickBooks: Staff → Edit → Payroll status → "Not on payroll".
      </p>
      <div className="flex flex-wrap gap-1.5">
        {workers.sort((a, b) => a.name.localeCompare(b.name)).map((w) => {
          const out = w.exempt || excludedIds.includes(w.id);
          return (
            <label key={w.id} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs cursor-pointer select-none ${out ? 'border-[#2D3748] text-slate-500' : 'border-emerald-700/50 text-slate-200'}`}
              title={w.exempt ? 'Exempt in staff profile — always skipped' : (out ? 'Left out of this push' : 'Included in this push')}>
              <input type="checkbox" className="accent-emerald-500" checked={!out} disabled={w.exempt} onChange={() => onToggle?.(w.id)} data-testid={`pw-include-${w.id}`} />
              <span>{w.name}</span>
              <span className="text-[10px]" style={{ color: w.exempt ? '#7BB8E8' : (w.errors && !out ? '#EF4444' : '#64748B') }}>
                {w.exempt ? 'exempt' : `${w.ready} day${w.ready === 1 ? '' : 's'}${w.errors ? ` · ${w.errors} blocked` : ''}`}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

// Push-to-QBO Dialog — preview + confirm timesheet push to QuickBooks.
export const PushToQboDialog = ({
  open, onClose, startDate, endDate,
  previewLoading, previewError, previewResult, pushResult,
  totalReady, totalSkipped, totalBlockers, totalAlreadyPushed, totalUnapproved, totalExempt = 0,
  excludedIds = [], onToggleExcluded,
  onRefreshPreview, onPush, pushing, pushProgress, onDownloadCsv, csvDownloading, token, toast,
}) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pw-push-dialog">
      <DialogHeader>
        <DialogTitle>Push timesheets to QuickBooks</DialogTitle>
        <DialogDescription>
          Pay period: {startDate} → {endDate}. Review the breakdown below before confirming.
        </DialogDescription>
      </DialogHeader>

      {previewLoading && (
        <div className="py-8 text-center text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Building preview…
        </div>
      )}

      {previewError && !previewLoading && (
        <div
          className="p-3 border border-red-700/40 bg-red-500/10 rounded text-sm text-red-200"
          data-testid="pw-preview-error"
        >
          <div className="flex items-start gap-2">
            <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold mb-0.5">Can&apos;t generate a QuickBooks preview</div>
              <div className="text-xs opacity-90">{previewError}</div>
              {/QuickBooks not connected/i.test(previewError) && (
                <div className="text-xs mt-2 opacity-80">
                  Connect QuickBooks Online under <b>Settings → Integrations</b>, then re-run this preview.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {previewResult && !pushResult && (
        <div className="space-y-3">
          <div className={`grid ${totalAlreadyPushed > 0 || totalExempt > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-2 text-xs`}>
            <Stat label="Ready" value={totalReady} accent="#00C896" testId="pw-preview-ready" />
            <Stat label="Skipped" value={totalSkipped} accent="#FFA000" testId="pw-preview-skipped" />
            <Stat label="Blockers" value={totalBlockers} accent={totalBlockers > 0 ? '#EF4444' : '#64748B'} testId="pw-preview-blockers" />
            {totalAlreadyPushed > 0 && (
              <Stat label="Already pushed" value={totalAlreadyPushed} accent="#7BB8E8" testId="pw-preview-already-pushed" />
            )}
            {totalExempt > 0 && (
              <Stat label="Exempt / left out" value={totalExempt} accent="#7BB8E8" testId="pw-preview-exempt" />
            )}
          </div>
          <WorkerIncludeList details={previewResult.details} excludedIds={excludedIds} onToggle={onToggleExcluded} />
          {totalAlreadyPushed > 0 && (
            <div className="p-2 border border-sky-700/40 bg-sky-500/5 rounded text-xs text-sky-300" data-testid="pw-already-pushed-note">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              {totalAlreadyPushed} worker-day{totalAlreadyPushed === 1 ? ' was' : 's were'} already sent to QuickBooks and will be skipped automatically — duplicate-push protection prevents double pay.
            </div>
          )}
          {totalUnapproved > 0 && (
            <div className="p-2 border border-amber-700/40 bg-amber-500/5 rounded text-xs text-amber-300" data-testid="pw-unapproved-note">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              {totalUnapproved} worker-day{totalUnapproved === 1 ? ' is' : 's are'} held back by the approval gate — hours must be payroll-approved or inside a locked period before they push. Approve them in Approvals, or lock the period (your sign-off), then re-run the preview.
            </div>
          )}
          {totalBlockers > 0 && (
            <div className="p-2 border border-red-700/40 bg-red-500/5 rounded text-xs text-red-300">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              Some entries can&apos;t push to QuickBooks yet. Fix the blockers (usually missing employees) then re-run the preview. Pushing now will skip these rows.
              <button type="button" className="ml-2 underline text-sky-300" data-testid="pw-ask-ai-blockers"
                onClick={() => window.dispatchEvent(new CustomEvent('ht:ask', { detail: { question: 'My payroll push preview shows blockers — explain each problem and the exact steps to fix them in HinesTime before pushing.' } }))}>
                Ask HinesTime how to fix ↗
              </button>
            </div>
          )}
          <DetailsTable details={previewResult.details} excludedIds={excludedIds} />
        </div>
      )}

      {pushResult && (
        <div className="space-y-3">
          <div className={`grid ${pushResult.already_pushed > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-2 text-xs`}>
            <Stat label="Pushed" value={pushResult.entries_pushed} accent="#00C896" testId="pw-result-pushed" />
            <Stat label="Errors" value={pushResult.errors} accent={pushResult.errors > 0 ? '#EF4444' : '#64748B'} testId="pw-result-errors" />
            <Stat label="Workers" value={pushResult.workers_synced} testId="pw-result-workers" />
            {pushResult.already_pushed > 0 && (
              <Stat label="Duplicates blocked" value={pushResult.already_pushed} accent="#7BB8E8" testId="pw-result-already-pushed" />
            )}
          </div>
          <div className="flex items-center gap-2 text-xs" data-testid="pw-result-verification">
            <span className="text-slate-400">Post-push check:</span>
            {pushResult.verification ? <VerificationBadge v={pushResult.verification} /> : <span className="text-slate-500">{pushResult.entries_pushed ? 'not run' : 'nothing was pushed'}</span>}
            {(pushResult.errors > 0 || pushResult.verification?.problems > 0) && (
              <button type="button" className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded border border-sky-700/50 text-sky-300 hover:bg-sky-500/10"
                onClick={() => window.dispatchEvent(new CustomEvent('ht:ask', { detail: { question: 'My last payroll push to QuickBooks had errors — what went wrong and how do I fix it step by step?' } }))}
                data-testid="pw-ask-ai-errors" title="Ask HinesTime reads this push result, open sync errors and the troubleshooting guide">
                <Sparkles className="w-3 h-3" /> Ask HinesTime about these errors
              </button>
            )}
          </div>
          <DetailsTable details={pushResult.details} />
        </div>
      )}

      {!pushResult && !previewLoading && (
        <div className="space-y-3 pt-3 border-t border-[#2D3748]">
          <EmployeePreflight token={token} startDate={startDate} endDate={endDate} onMapped={onRefreshPreview} />
          <PushHistory token={token} DetailsTable={DetailsTable} />
          <QboReconcile token={token} startDate={startDate} endDate={endDate} />
          <PushedDayLocks token={token} startDate={startDate} endDate={endDate} toast={toast} />
        </div>
      )}

      <DialogFooter className="gap-2">
        {!pushResult ? (
          <>
            <Button variant="outline" onClick={onClose} data-testid="pw-push-cancel">Cancel</Button>
            <Button variant="outline" onClick={onDownloadCsv} disabled={csvDownloading} data-testid="pw-csv-backup-btn"
              title="Download a CSV backup of this payroll run (Regular/OT per worker per day) — your fallback if the QuickBooks push fails">
              {csvDownloading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />} CSV backup
            </Button>
            <Button variant="outline" onClick={onRefreshPreview} disabled={previewLoading} data-testid="pw-preview-refresh">
              <RefreshCw className="w-3 h-3 mr-1" /> Re-run preview
            </Button>
            <Button
              className="bg-[#00C896] hover:bg-green-700 text-white"
              disabled={!previewResult || pushing || totalReady === 0}
              onClick={onPush}
              data-testid="pw-confirm-push"
            >
              {pushing
                ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Pushing…{pushProgress ? ` ${pushProgress} day${pushProgress === 1 ? '' : 's'} sent` : ''}</>
                : <><Send className="w-3 h-3 mr-1" /> Confirm &amp; push {totalReady} {totalReady === 1 ? 'entry' : 'entries'}</>}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} className="bg-[#1E88E5] hover:bg-blue-700" data-testid="pw-push-done">Done</Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
